import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { api } from '../../lib/api'
import {
  BookOpen,
  CheckCircle,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Target,
  Clock,
  Lightbulb,
} from 'lucide-react'
import { useState } from 'react'

interface TestPrepItem {
  id: number
  concept_name: string
  question_text: string
  correct_answer: string
  explanation: string
  studied: boolean
  studied_at: string | null
  priority_score: number
  created_at: string | null
  homework_session_id: number
}

interface TestPrepResponse {
  items_by_subject: Record<string, TestPrepItem[]>
  subjects: string[]
  total_count: number
  unstudied_count: number
}

export default function TestPrepGuide() {
  const { studentId } = useParams<{ studentId: string }>()
  const queryClient = useQueryClient()
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery<TestPrepResponse>({
    queryKey: ['test-prep', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/homework/students/${studentId}/test-prep`)
      return response.data
    },
  })

  const markStudiedMutation = useMutation({
    mutationFn: async (itemId: number) => {
      const response = await api.post(`/api/homework/test-prep/${itemId}/mark-studied`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-prep', studentId] })
    },
  })

  const toggleItem = (id: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-surface-card rounded"></div>
        <div className="h-40 bg-surface-card rounded-xl"></div>
        <div className="h-60 bg-surface-card rounded-xl"></div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <p className="text-gray-400">Failed to load test prep guide</p>
        <Link to="/parent" className="text-quest-primary hover:underline mt-4 inline-block">
          Return to Dashboard
        </Link>
      </div>
    )
  }

  const subjects = data.subjects
  const activeSubject = selectedSubject || subjects[0]
  const items = activeSubject ? data.items_by_subject[activeSubject] || [] : []

  // Separate studied vs unstudied
  const unstudied = items.filter((i) => !i.studied)
  const studied = items.filter((i) => i.studied)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/parent/students/${studentId}`}
          className="p-2 rounded-lg bg-surface-card hover:bg-surface-hover transition"
        >
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="w-6 h-6 text-quest-primary" />
            Test Prep Guide
          </h1>
          <p className="text-gray-400">Review these concepts before the test</p>
        </div>
      </div>

      {/* Summary Card */}
      <div className="card">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-3xl font-bold text-quest-primary">{data.total_count}</div>
            <div className="text-sm text-gray-400">Total Items</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-yellow-400">{data.unstudied_count}</div>
            <div className="text-sm text-gray-400">To Review</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-green-400">
              {data.total_count - data.unstudied_count}
            </div>
            <div className="text-sm text-gray-400">Completed</div>
          </div>
        </div>
      </div>

      {/* Subject Tabs */}
      {subjects.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {subjects.map((subject) => {
            const subjectItems = data.items_by_subject[subject] || []
            const subjectUnstudied = subjectItems.filter((i) => !i.studied).length
            return (
              <button
                key={subject}
                onClick={() => setSelectedSubject(subject)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap flex items-center gap-2 ${
                  activeSubject === subject
                    ? 'bg-quest-primary text-white'
                    : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
                }`}
              >
                {subject}
                {subjectUnstudied > 0 && (
                  <span className="bg-yellow-500/20 text-yellow-400 text-xs px-1.5 py-0.5 rounded">
                    {subjectUnstudied}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {/* No Items */}
      {items.length === 0 && (
        <div className="card text-center py-12">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
          <h2 className="text-xl font-bold mb-2">All Caught Up!</h2>
          <p className="text-gray-400">
            No items to review for {activeSubject || 'this subject'}.
          </p>
        </div>
      )}

      {/* Unstudied Items */}
      {unstudied.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-yellow-400" />
            <h2 className="text-lg font-bold">Need to Review</h2>
            <span className="text-gray-400">({unstudied.length})</span>
          </div>

          {unstudied.map((item) => (
            <PrepItemCard
              key={item.id}
              item={item}
              expanded={expandedItems.has(item.id)}
              onToggle={() => toggleItem(item.id)}
              onMarkStudied={() => markStudiedMutation.mutate(item.id)}
              isMarking={markStudiedMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Studied Items */}
      {studied.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <h2 className="text-lg font-bold">Already Reviewed</h2>
            <span className="text-gray-400">({studied.length})</span>
          </div>

          {studied.map((item) => (
            <PrepItemCard
              key={item.id}
              item={item}
              expanded={expandedItems.has(item.id)}
              onToggle={() => toggleItem(item.id)}
              studied
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface PrepItemCardProps {
  item: TestPrepItem
  expanded: boolean
  onToggle: () => void
  onMarkStudied?: () => void
  isMarking?: boolean
  studied?: boolean
}

function PrepItemCard({
  item,
  expanded,
  onToggle,
  onMarkStudied,
  isMarking,
  studied,
}: PrepItemCardProps) {
  return (
    <div className={`card ${studied ? 'opacity-60' : ''}`}>
      {/* Header */}
      <button onClick={onToggle} className="w-full flex items-center justify-between text-left">
        <div className="flex items-center gap-3">
          {studied ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <Lightbulb className="w-5 h-5 text-yellow-400" />
          )}
          <div>
            <div className="font-medium">{item.concept_name}</div>
            <div className="text-sm text-gray-400">
              {item.studied_at && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Studied {new Date(item.studied_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Question */}
          <div>
            <div className="text-sm text-gray-500 mb-1">Example Question:</div>
            <div className="bg-surface-card p-3 rounded-lg">{item.question_text}</div>
          </div>

          {/* Answer */}
          <div>
            <div className="text-sm text-gray-500 mb-1">Correct Answer:</div>
            <div className="bg-green-500/10 border border-green-500/30 p-3 rounded-lg text-green-400">
              {item.correct_answer}
            </div>
          </div>

          {/* Explanation */}
          {item.explanation && (
            <div>
              <div className="text-sm text-gray-500 mb-1">How to Solve:</div>
              <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg text-blue-300">
                {item.explanation}
              </div>
            </div>
          )}

          {/* Mark Studied Button */}
          {!studied && onMarkStudied && (
            <button
              onClick={onMarkStudied}
              disabled={isMarking}
              className="w-full py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <CheckCircle className="w-5 h-5" />
              {isMarking ? 'Marking...' : 'Mark as Reviewed'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
